import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { invalidateClosedInspectionSet } from "@/lib/swingLookup";

/**
 * useActiveSwing — SERVER-FIRST version.
 *
 * The active swing now lives on the backend (`/api/swings`) so every device
 * that belongs to the same crew sees the same state — matching how the
 * mobile app works.
 *
 * • `swing`      = the server-side active swing this user is currently on
 *                  (or null if none). Shape mirrors `/api/swings`:
 *                    { id, crew_id, crew_name, supervisor_username,
 *                      swing_label, start_date, end_date, status,
 *                      inspection_ids, progress, ... }
 * • `start(shift, meta)` POSTs a new swing for the crew.
 * • `finish()`   PATCHes the active swing to `status="closed"`.
 * • `refresh()`  forces an immediate re-poll.
 *
 * Cross-device sync: polls `/api/swings` every 5 s while the tab is visible.
 * As soon as a supervisor closes the swing on the mobile app or portal,
 * every other browser/device on that crew flips back to the Start Swing
 * hero within ~5 s. Josh (Jul 10) asked for the crew's home list to clear
 * "instantly" when a supervisor ends the swing — the 15 s cadence used
 * previously left crew members staring at a stale list for up to 15 s.
 */
const POLL_MS = 5000;
const LEGACY_KEY = "shiftlog.active_swing";

// Wipe the OLD localStorage-based swings so the new server-backed swing
// wins from the first render.
try { localStorage.removeItem(LEGACY_KEY); } catch { /* noop */ }

export function useActiveSwing(site, user) {
  // Backwards compat — old callers pass only `site`. Newer callers pass
  // `(site, user)` so we can filter by crew / supervisor.
  const [swing, setSwing] = useState(null);
  const [loading, setLoading] = useState(true);
  const runningRef = useRef(false);
  const disposedRef = useRef(false);

  const pickActive = useCallback((list) => {
    if (!Array.isArray(list) || list.length === 0) return null;
    const active = list.filter((s) => String(s.status || "").toLowerCase() === "active");
    if (active.length === 0) return null;

    // Prefer a swing whose supervisor is this user OR whose crew matches
    // this user's crew. If neither matches, fall back to any active swing
    // for this site (there's usually only one running).
    const uname = (user?.username || "").toLowerCase();
    const uid = user?.id || user?.user_id;
    const ucrewId = user?.crew_id || user?.assigned_crew_id;
    const ucrewName = String(user?.crew || user?.crew_name || user?.assigned_crew_name || "").toLowerCase();

    const mineBySupervisor = active.find((s) =>
      (uname && String(s.supervisor_username || "").toLowerCase() === uname) ||
      (uid && s.supervisor_id === uid),
    );
    if (mineBySupervisor) return mineBySupervisor;

    const mineByCrew = active.find((s) =>
      (ucrewId && s.crew_id === ucrewId) ||
      (ucrewName && String(s.crew_name || "").toLowerCase() === ucrewName),
    );
    if (mineByCrew) return mineByCrew;

    // Otherwise, use the most recently created active swing at this site.
    return active.slice().sort((a, b) =>
      new Date(b.created_at || 0) - new Date(a.created_at || 0),
    )[0];
  }, [user?.username, user?.id, user?.user_id, user?.crew_id, user?.assigned_crew_id, user?.crew, user?.crew_name, user?.assigned_crew_name]);

  const refresh = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      const { data } = await api.get("/swings", { params: { limit: 40 } });
      const list = Array.isArray(data) ? data : (data?.items || data?.results || []);
      if (disposedRef.current) return;
      setSwing(pickActive(list));
    } catch {
      /* silent — keep whatever we had */
    } finally {
      runningRef.current = false;
      setLoading(false);
    }
  }, [pickActive]);

  useEffect(() => {
    disposedRef.current = false;
    refresh();
    const iv = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      refresh();
    }, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      disposedRef.current = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refresh]);

  const start = useCallback(async (shift, meta = {}) => {
    const now = new Date();
    const iso = now.toISOString();
    const week = isoWeek(now);
    const label = `Swing · Week ${week} · ${iso.slice(0, 10)} · ${shift === "night" ? "Night" : "Day"}`;

    const body = {
      client_op_id: `swing-${Date.now()}`,
      swing_label: label,
      start_date: iso.slice(0, 10),
      end_date: iso.slice(0, 10),
      work_area: meta.work_area || "field",
      shift,
      notes: `Started by ${meta.startedBy || user?.name || user?.username || "portal user"}`,
    };
    if (meta.crewId || user?.crew_id || user?.assigned_crew_id) {
      body.crew_id = meta.crewId || user?.crew_id || user?.assigned_crew_id;
    }
    if (meta.crew || user?.crew || user?.crew_name || user?.assigned_crew_name) {
      body.crew_name = meta.crew || user?.crew || user?.crew_name || user?.assigned_crew_name;
    }

    try {
      const { data } = await api.post("/swings", body);
      setSwing(data);
      // Fire-and-forget refresh so any server-side augmentation is picked up.
      setTimeout(refresh, 400);
      return data;
    } catch (ex) {
      // Re-throw so the UI can surface the error.
      throw ex;
    }
  }, [refresh, user?.name, user?.username, user?.crew_id, user?.assigned_crew_id, user?.crew, user?.crew_name, user?.assigned_crew_name]);

  const finish = useCallback(async () => {
    if (!swing?.id) return null;
    try {
      // First choice: PATCH the resource (standard REST).
      await api.patch(`/swings/${swing.id}`, {
        status: "closed",
        closed_at: new Date().toISOString(),
      });
    } catch {
      // Some backends prefer a dedicated action endpoint.
      try {
        await api.post(`/swings/${swing.id}/close`, {});
      } catch { /* silent; refresh will reveal current state */ }
    }
    setSwing(null);
    // Bust the "which inspections belong to a closed swing" cache so anyone
    // who drills into an inspection from THIS swing immediately after the
    // finish (e.g. supervisor eyeballing "how much did we actually get to?")
    // sees the view-only lock — no 10 s stale window.
    invalidateClosedInspectionSet();
    setTimeout(refresh, 400);
    return swing;
  }, [swing, refresh]);

  return { swing, loading, start, finish, refresh, isoWeek };
}

function isoWeek(d) {
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  return Math.ceil((((dt - yearStart) / 86400000) + 1) / 7);
}
